import { useMemo, ReactNode } from 'react';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider as BaseApolloProvider,
  gql,
  InMemoryCache,
  ServerError,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { createUploadLink } from 'apollo-upload-client';
import { useMsal, IMsalContext } from '@azure/msal-react';
import { getLocale } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import Config from '@monorepo/shared/src/config';
import { getAccessToken } from '@monorepo/shared/src/config/educatorApp/auth';
import { Headers } from '@monorepo/shared/src/types';
import { hasAuthCookies, removeAuthCookies } from '@monorepo/shared/src/util/auth-cookie-util';
import { sendGraphErrorLog, formatError } from '@monorepo/shared/src/util/telemetry-util';
import { MaybeWrapper } from '@lwtears/lwt-common-frontend/lib/@common/util/maybe-util';
import loginRedirect from '@lwtears/lwt-common-frontend/lib/Login/LoginRedirect';
import { getAuthHeaders, getSessionHeaders } from '@lwtears/lwt-common-frontend/lib/@common';
import WithQuery from '../components/common/WithQuery';
import initApolloStore from '../graph/queries/init-apollo-global-store';
import useUserStore from '../graph/hooks/use-user';
import possibleTypesEducator from '../graph/possibleType-educator.json';
import possibleTypesStudent from '../graph/possibleType-student.json';
import { isStudentApp } from '../util/app-util';
import { useErrorActions } from './ErrorProvider';

const {
  REACT_APP_APP_NAME: appName,
  REACT_APP_APP_VERSION: appVersion,
  REACT_APP_BUILD_NUMBER: appBuild,
} = process.env;

const LOG_OPERATION_NAMES = ['logApiError', 'logApplicationError'];

const setupClient = (
  azureContext: IMsalContext,
  useSSO?: boolean,
  setError?: (status?: number, message?: string, operationName?: string | null) => void
): ApolloClient<NormalizedCacheObject> => {
  const possibleTypes = isStudentApp ? possibleTypesStudent : possibleTypesEducator;
  const cache = new InMemoryCache({
    ...(possibleTypes && { possibleTypes: possibleTypes.possibleTypes }),
    typePolicies: {
      Assignment: {
        fields: {
          groups: {
            merge: false,
          },
        },
      },
    },
  });

  const authLink = setContext(async () => {
    let authHeaders;
    if (!useSSO) {
      authHeaders = getSessionHeaders();
    } else if (hasAuthCookies()) {
      authHeaders = getAuthHeaders();
    } else {
      const response = await getAccessToken(azureContext);

      // The token might not be ready yet, so we must abort the request.
      if (!response) return Promise.reject(new Error('Aborted request: auth token not ready.'));

      authHeaders = { ...(response && { Authorization: `Bearer ${response.accessToken}` }) };
    }

    const locale = getLocale();
    const headers = {
      headers: {
        [Headers.APP]: appName,
        [Headers.APP_VERSION]: appVersion,
        [Headers.APP_BUILD]: appBuild || 'dev',
        [Headers.LOCALE]: locale,
        'Apollo-Require-Preflight': 'true', // needed for file uploads
        ...authHeaders,
      },
    };
    return headers;
  });

  const link = createUploadLink({
    uri: Config.graphUrl,
  });

  const errorLink = onError(({ graphQLErrors = [], networkError = {}, operation }) => {
    if ((networkError as ServerError)?.statusCode === 401) {
      if (useSSO) {
        removeAuthCookies();
        azureContext.instance.logoutRedirect();
      } else {
        loginRedirect();
      }
    }

    // don't try to log if no internet
    if (!(networkError as ServerError)?.statusCode && !graphQLErrors.length) return;

    if (LOG_OPERATION_NAMES.includes(operation.operationName)) return;

    let errorHandled = false;

    // log non-network-failure, non-auth related errors
    for (const graphQLError of graphQLErrors) {
      const errorEvent = formatError(graphQLError, networkError, operation);
      try {
        setError?.(errorEvent.status, errorEvent?.data?.message, operation.operationName);
        sendGraphErrorLog(client, {
          ...(errorEvent ?? {}),
          timestamp: new Date().toISOString(),
        });
      } catch (err) {
        console.error(err);
      }
      errorHandled = true;
    }

    // Handle plain network/server errors (no graphQLErrors)
    if (!errorHandled && (networkError as ServerError)?.statusCode) {
      const status = (networkError as ServerError).statusCode;
      const message = (networkError as Error)?.message;
      setError?.(status, message, operation.operationName);
    }
  });

  const client = new ApolloClient({
    cache,
    link: ApolloLink.from([authLink, errorLink, link]),
    resolvers: {},
  });

  const store = initApolloStore();
  // TODO configure cache
  cache.writeQuery({
    query: gql`
      query User {
        user {
          __typename
        }
      }
    `,
    data: store,
  });

  return client;
};

type MatManApolloProviderProps = {
  children?: ReactNode;
  useSSO?: boolean;
  client: ApolloClient<NormalizedCacheObject>;
  azureContext: IMsalContext;
};

const MatManApolloProvider: React.FC<MatManApolloProviderProps> = ({ children }) => {
  const [{ isAuthenticating }] = useUserStore();

  return <WithQuery loading={isAuthenticating}>{children}</WithQuery>;
};

const ApolloProvider: React.FC<{ children?: ReactNode; useSSO?: boolean }> = ({
  children,
  useSSO,
}) => {
  const { setError } = useErrorActions();
  const azureContext = useMsal();
  const client = useMemo(
    () => setupClient(azureContext, useSSO, setError),
    [azureContext, useSSO, setError]
  );

  const MaybeMatManApolloProvider = MaybeWrapper<MatManApolloProviderProps>(MatManApolloProvider);

  return (
    <BaseApolloProvider client={client}>
      <MaybeMatManApolloProvider
        shouldRender={!isStudentApp}
        client={client}
        useSSO={useSSO}
        azureContext={azureContext}
      >
        {children}
      </MaybeMatManApolloProvider>
    </BaseApolloProvider>
  );
};
export default ApolloProvider;
